import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ChangeEvent, FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createAndInviteUserRequest, updateUserPreferencesRequest } from "../../../../../actions";
import { UserGroupDescription, UserGroupLabel, UserGroups } from "../../../../../enums/user-groups";
import { IRootReducer } from "../../../../../reducers";
import {
  CleanPhoneNumberUtil,
  ValidateEmailUtil,
  ValidatePhoneNumberUtil,
} from "../../../../../utils/string-utils";
import { useAuthContext } from "../../../../../core/auth-context";
import { JogId, AgencyId, Agency, AgencyIds, GetAgency } from "../../../../../reducers/states/agency";
import ViewEditSelect from "../../../../common/view-edit-select";
import ViewEditAgencyAutocomplete from "../../../../common/view-edit-agency-autocomplete";
import ViewEditSelectJog from "../../../../common/view-edit-select-jog";
import { DefaultUserPreferences } from "../../../../../sagas/user-info";
import { useJogs } from "../../../../../hooks/jogs";

export const getJogProps = (agency: Agency | null | undefined): { agencyJogId: string, isReadOnly: boolean } => {
  // Special case for KCM, to only allow adding those users to SAJOG, even though KCM is part of multiple JOGs
  // for dispatch event display/viewing. It might be nice to do this differently, based on properties of Agencies/JOGs,
  // but that's more work and complication than it's worth for this one special case.
  const jogIds = agency?.id === AgencyIds.KingCountyMetro
    ? JogId.SeattleArea
    : agency?.jogIds || [];

  const agencyJogId = jogIds.length === 1 ? jogIds[0] : JogId.NOT_SELECTED; // User must choose unless there is only one option.
  const isReadOnly = jogIds?.length < 2; // ReadOnly when an agency is associated with only one JOG.

  return {
    agencyJogId: agencyJogId,
    isReadOnly,
  }
}

const InviteUser: FC<{ setInvitedEmail: (invitedEmail: string) => void }> = (props) => {
  // Form Input: User details
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [agency, setAgency] = useState("");
  const [jogId, setJogId] = useState("");
  const [userRoles, setUserRoles] = useState([UserGroups.VCC_BASIC as string]);

  // User Preferences
  const [selectedJogs, setSelectedJogs] = useState<string[]>([]);
  const [notificationJogs, setNotificationJogs] = useState<string[]>([]);

  // Form controls
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = useState(false);
  const [inviteBtnDisabled, setInviteBtnDisabled] = useState(true);
  const [agencyReadonly, setAgencyReadOnly] = useState(false);
  const [jogReadonly, setJogReadOnly] = useState(true);
  const [showAdditionalJogs, setShowAdditionalJogs] = useState(false);

  const dispatch = useDispatch();
  const store = useSelector((state: IRootReducer) => state.adminReducer);
  const currentUserStore = useSelector((state: IRootReducer) => state.userInfoReducer.currentUserInfo);
  const agencyStore = useSelector((state: IRootReducer) => state.agencyReducer);

  const { authState } = useAuthContext();

  const jogs = useJogs();
  const userAgency = GetAgency(agencyStore.agenciesById, agency);
  const userAgencyJogs = jogs.getAgencyJogs(userAgency);

  const MaxSelectedJogCount = 5;

  useEffect(() => {
    // Disable invite if phone number is invalid. Blank is ok.
    if (phoneNumber !== "" && !ValidatePhoneNumberUtil(phoneNumber)) {
      setInviteBtnDisabled(true);
      return;
    }

    // Disable invite if email is blank or invalid
    if (email !== "" && !ValidateEmailUtil(email)) {
      setInviteBtnDisabled(true);
      return;
    }

    // Disable invite if any required field is blank
    setInviteBtnDisabled(
      email === "" ||
      firstName === "" ||
      lastName === "" ||
      agency === "" ||
      jogId === JogId.NOT_SELECTED || jogId === JogId.Other
    );
  }, [agency, jogId, email, firstName, lastName, phoneNumber]);

  const updateJogValue = (agencyId: AgencyId) => {
    const agency = GetAgency(agencyStore.agenciesById, agencyId);
    const { agencyJogId, isReadOnly } = getJogProps(agency);

    setJogId(agencyJogId);
    setJogReadOnly(isReadOnly);

    updateAdditionalJogs(agencyJogId);
    updateAdditionalNotifications(agencyJogId);
  }

  const updateAdditionalJogs = (newUserJogId: string) => {
    setSelectedJogs((current) => {
      const jogIds = new Set(current);
      jogIds.delete(jogId);
      jogIds.add(newUserJogId);
      const newJogIds = [...jogIds.values()];

      return newJogIds;
    });
  }

  const updateAdditionalNotifications = (newUserJogId: string) => {
    setNotificationJogs((current) => {
      const jogIds = new Set(current);
      jogIds.delete(jogId);
      jogIds.add(newUserJogId);
      const newJogIds = [...jogIds.values()];

      return newJogIds;
    });
  }

  const resetForm = () => {
    // UI control
    setOpen(false);
    setAgencyReadOnly(false);
    setJogReadOnly(true);
    setShowAdditionalJogs(false);

    // User fields
    setEmail("");
    setFirstName("");
    setLastName("");
    setPhoneNumber("");
    setAgency("");
    setJogId("");
    setSelectedJogs([]);
    setNotificationJogs([]);
  }

  const handleOpen = () => {
    if (authState.userGroups.toString().includes(UserGroups.AGENCY_ADMIN)
      && !authState.userGroups.toString().includes(UserGroups.SITE_ADMIN)) {
      setAgency(currentUserStore.agency);
      setAgencyReadOnly(true);
      updateJogValue(currentUserStore.agency);
    }

    setOpen(true);
  };

  const handleCancel = () => {
    resetForm();
  };

  const saveJogPreferences = () => {
    const jogFilters = selectedJogs.filter(id => !!id); // clear falsy/undefined/empty

    // Ensure primary jogId is the last value added to array, so the default map viewport that's loaded is for the user's primary JOG.
    const primaryJogIndex = jogFilters.findIndex((id) => id === jogId);
    jogFilters.push(...jogFilters.splice(primaryJogIndex, 1));

    // Get agency filters for all JOGs
    const agencyFilters = new Set<string>(); // Use Set to avoid duplications across JOGs (e.g., WSP districts that span multiple JOGs)
    jogFilters.forEach((id) => {
      jogs.get(id)?.agencies.forEach(agency => agency.isFilter && agencyFilters.add(agency.id));
    });

    //const groupIds = saveNotificationSettings();

    // Saving the preferences works even if the user hasn't been created yet, so it can be dispatched without waiting for the success of creating a user.
    // If the user creation fails, it will work to repeat the invite process becasue this is a PUT and will work repeatedly.
    dispatch(updateUserPreferencesRequest(
      {
        ...DefaultUserPreferences,
        agencyFilters: [...agencyFilters.values()],
        jogFilters,
        jogNotificationsPostConf: notificationJogs,
      },
      email
    ));
  }

  const handleInviteAndClose = () => {
    let cleanNumber = phoneNumber;
    if (phoneNumber !== "") {
      cleanNumber = CleanPhoneNumberUtil(phoneNumber);
    }

    dispatch(
      createAndInviteUserRequest(
        email,
        firstName,
        lastName,
        cleanNumber,
        agency,
        jogId,
        userRoles,
        (authState.email ?? "").toLowerCase(),
      )
    );

    props.setInvitedEmail(email);

    saveJogPreferences();

    setOpen(false);

    // Didn't need this call to restForm() here before adding the "more complex" custom components for,
    // agency, Primary JOG, selectedJogs, notificationJogs, and also "showAdditionalJogs"
    // Somehow it seemed the dialog was clearing it's form elements when setOpen(false) was called, but not these newer elements.
    // Is there a way to update the custom components so they'd get cleared in the same way?
    resetForm();
  };

  const handleAgencySelectionChanged = (agencyId: AgencyId | null, elementId: string) => {
    setAgency(agencyId || "");
    updateJogValue(agencyId || "");
  };

  const handleUserJogSelectionChanged = (newJogId: JogId, elementId: string) => {
    setJogId(newJogId);

    updateAdditionalJogs(newJogId);
    updateAdditionalNotifications(newJogId);
  };

  const handleSelectedJogsChange = (value: string[], field: string) => {
    setSelectedJogs(value);
  };

  const handleNotificationJogsChange = (value: string[], field: string) => {
    setNotificationJogs(value);
  };

  const handleRoleSelect = (event: ChangeEvent<HTMLInputElement>) => {
    let newRoles = [...userRoles];
    if (
      event.target.checked === true &&
      newRoles.indexOf(event.target.id) === -1
    ) {
      newRoles.push(event.target.id);
    } else {
      newRoles = newRoles.filter((r) => r !== event.target.id);
    }

    setUserRoles(newRoles);
  };

  return (
    <>
      <Tooltip title="Invite a new user to the VCC">
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpen}
          style={{ minWidth: "150px", color: "white" }}
        >
          Invite User
        </Button>
      </Tooltip>
      <Dialog
        fullScreen={fullScreen}
        fullWidth
        open={open}
        onClose={handleCancel}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">Invite User</DialogTitle>
        <DialogContent className="invite-edit-user">
          <DialogContentText>
            Enter an email and user information to invite a new user to the VCC
          </DialogContentText>
          <TextField
            autoFocus
            required
            variant="outlined"
            margin="dense"
            id="email"
            label="Email Address"
            type="email"
            fullWidth
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            required
            variant="outlined"
            margin="dense"
            id="firstName"
            label="First Name"
            type="string"
            fullWidth
            onChange={(e) => setFirstName(e.target.value)}
          />
          <TextField
            required
            variant="outlined"
            margin="dense"
            id="lastName"
            label="Last Name"
            type="string"
            fullWidth
            onChange={(e) => setLastName(e.target.value)}
          />
          <TextField
            variant="outlined"
            margin="dense"
            id="phoneNumber"
            label="Phone Number"
            type="phoneNumber"
            fullWidth
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
          <ViewEditAgencyAutocomplete
            disableShrinkLabel={true}
            isEdit={!agencyReadonly}
            required={true}
            size="medium"
            value={agency}
            handleInputChange={handleAgencySelectionChanged}
          />
          {/* todo: jcavner: future refactor: possibly use ViewEditSelectJog here, but it'll take some time to make sure it's working correctly for this simpler, single value case. */}
          <ViewEditSelect
            props={{
              disableShrinkLabel: true,
              id: "jogId",
              label: "Primary JOG",
              isEdit: !jogReadonly,
              required: true,
              size: "medium",
              tooltip: "User's primary JOG. Users will receive notifications for incidents created in primary JOG.",
              value: jogId !== JogId.Other ? jogId : JogId.NOT_SELECTED,
              handleInputChange: handleUserJogSelectionChanged,
            }}
          >
            {userAgencyJogs.map(
              jog => <MenuItem key={jog.id} value={jog.id}>{jog.name}</MenuItem>
            )}
          </ViewEditSelect>
          <Tooltip title="If applicable, check this box to see additional inputs.">
            <FormControlLabel
              control={
                <Checkbox
                  checked={showAdditionalJogs}
                  onChange={() => setShowAdditionalJogs(current => !current)}
                />
              }
              label="User should monitor multiple JOGs"
            />
          </Tooltip>
          {showAdditionalJogs ? (
            <>
              <ViewEditSelectJog
                disableShrinkLabel
                id="selectedJogs"
                isEdit
                label="Primary & Additional JOGs"
                maxSelectedCount={MaxSelectedJogCount}
                multiple
                size="medium"
                tooltip={`If applicable, select up to ${MaxSelectedJogCount - 1} additional JOGs this user is expected to monitor. The user can also change this later.`}
                userPrimaryJogId={jogId}
                value={selectedJogs}
                handleInputChange={handleSelectedJogsChange}
              />
              <ViewEditSelectJog
                disableShrinkLabel
                id="additionalNotifications"
                isEdit
                label="Incident Notifications"
                maxSelectedCount={MaxSelectedJogCount}
                multiple
                size="medium"
                userPrimaryJogId={jogId}
                tooltip={`If applicable, select up to ${MaxSelectedJogCount - 1} additional JOGs this user should receive notifications for when new VCC incidents are created. The user can also change this later.`}
                value={notificationJogs}
                handleInputChange={handleNotificationJogsChange}
              />
            </>
          ) : undefined}
          <Divider sx={{ margin: "1em 0" }} />
          <FormGroup sx={{ margin: "10px" }}>
            <Box>User Roles</Box>
            <Grid container>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox checked={true} id={UserGroups.VCC_BASIC} />
                  }
                  label={UserGroupLabel.VCC_BASIC}
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip title={UserGroupDescription.VCC_BASIC}>
                  <InfoOutlinedIcon color="action" sx={{ marginTop: "10px" }} />
                </Tooltip>
              </Grid>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id={UserGroups.INC_CONTRIBUTOR}
                      onChange={handleRoleSelect}
                    />
                  }
                  label={UserGroupLabel.INC_CONTRIBUTOR}
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip title={UserGroupDescription.INC_CONTRIBUTOR}>
                  <InfoOutlinedIcon color="action" sx={{ marginTop: "10px" }} />
                </Tooltip>
              </Grid>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id={UserGroups.INC_MANAGER}
                      onChange={handleRoleSelect}
                    />
                  }
                  label={UserGroupLabel.INC_MANAGER}
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip title={UserGroupDescription.INC_MANAGER}>
                  <InfoOutlinedIcon color="action" sx={{ marginTop: "10px" }} />
                </Tooltip>
              </Grid>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id={UserGroups.INC_RECORDS_MANAGER}
                      onChange={handleRoleSelect}
                    />
                  }
                  label={UserGroupLabel.INC_RECORDS_MANAGER}
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip title={UserGroupDescription.INC_RECORDS_MANAGER}>
                  <InfoOutlinedIcon color="action" sx={{ marginTop: "10px" }} />
                </Tooltip>
              </Grid>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={userRoles.indexOf(UserGroups.PIO) > -1}
                      id={UserGroups.PIO}
                      onChange={handleRoleSelect}
                    />
                  }
                  label={UserGroupLabel.PIO}
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip title={UserGroupDescription.PIO}>
                  <InfoOutlinedIcon color="action" sx={{ marginTop: "10px" }} />
                </Tooltip>
              </Grid>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={userRoles.indexOf(UserGroups.MAJOR_EVENT_CONTRIBUTOR) > -1}
                      id={UserGroups.MAJOR_EVENT_CONTRIBUTOR}
                      onChange={handleRoleSelect}
                    />
                  }
                  label={UserGroupLabel.MAJOR_EVENT_CONTRIBUTOR}
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip title={UserGroupDescription.MAJOR_EVENT_CONTRIBUTOR}>
                  <InfoOutlinedIcon color="action" sx={{ marginTop: "10px" }} />
                </Tooltip>
              </Grid>
              <Grid item xs={11}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id={UserGroups.AGENCY_ADMIN}
                      onChange={handleRoleSelect}
                    />
                  }
                  label={UserGroupLabel.AGENCY_ADMIN}
                />
              </Grid>
              <Grid item xs={1}>
                <Tooltip title={UserGroupDescription.AGENCY_ADMIN}>
                  <InfoOutlinedIcon color="action" sx={{ marginTop: "10px" }} />
                </Tooltip>
              </Grid>
              <Grid item xs={11}>
                {(authState.userGroups.indexOf(UserGroups.SITE_ADMIN) > -1) && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={userRoles.indexOf(UserGroups.SITE_ADMIN) > -1}
                        id={UserGroups.SITE_ADMIN}
                        onChange={handleRoleSelect}
                      />
                    }
                    label={UserGroupLabel.SITE_ADMIN}
                  />)}
              </Grid>
            </Grid>
          </FormGroup>
          <DialogActions>
            <Button onClick={handleCancel} color="inherit" variant="contained">
              Cancel
            </Button>
            <Button
              disabled={inviteBtnDisabled}
              onClick={handleInviteAndClose}
              color="primary"
              variant="contained"
            >
              Invite
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InviteUser;

